/**
 * Design D's start screen (Matt approved the mockups 2026-10-22 16:36; docs/ux/ux1-notes.md for design D): the question,
 * three goal cards (the payslip check the one primary), the full approved privacy line (always shown; the public
 * counter line under it, always shown: Matt 2026-10-23 19:02); "How it works" is the footer's (Matt, 2026-10-23 02:08). Static: the start screen loads it (CLAUDE.md "Size budget"); every later step's parts are in the lazy
 * flow chunk (./steps.ts). Texts through `tr`, actions as callbacks from src/ui/app.ts.
 */
import { startIcon as flowIcon, type StartIcon as FlowIcon } from './icons';
import { canDragDrop, dragHasFiles } from './dragModel';
import { splitPrivacyTitle } from './model';

type El = <K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Record<string, string>, ...children: (Node | string)[]) => HTMLElementTagNameMap[K];
type Tr = (key: string, params?: Record<string, string | number>) => string;

function btn(el: El, label: (Node | string)[], onClick: () => void, cls: string, id?: string): HTMLButtonElement {
  const b = el('button', { type: 'button', class: cls, ...(id ? { id } : {}) }, ...label);
  b.addEventListener('click', onClick);
  return b;
}

export interface StartSpec {
  el: El;
  tr: Tr;
  /** The language's own name (kept for callers; the translation card's subline no longer names it, Matt 2026-10-23 02:08). */
  languageName: string;
  /** The approved privacy line (app.privacyLine.*) with its "turn off" link already in place, as text parts. */
  privacy: { text: string; link?: { text: string; href: string } };
  onPayslip: () => void;
  /** Absent while Form 106 isn't offered in this build. */
  onForm106?: () => void;
  onTranslate: () => void;
  /** "Why check?": reopens the opening page (./opening.ts; MATT GO 2026-10-23 00:56). Absent: no link. */
  onWhy?: () => void;
  /**
   * Files dropped on a goal card (computers only: a fine pointer and drag events): the goal starts with them added.
   * Absent: the cards take no drops.
   */
  onDropFiles?: (goal: 'payslip' | 'form106' | 'translate', files: FileList | null) => void;
  /** Live tools for the closed "More tools" drawer (a tool is listed only when its flag is on; none: no drawer). */
  tools?: { id: string; title: string; sub: string; route: string }[];
  /** The public counter line, already worded (empty without a counter or a total). */
  checks: (Node | string)[];
}

/** Text with `{off}` replaced by a link (the privacy line's "turn off"); a text without it gets the link after it. */
function withLink(el: El, text: string, link?: { text: string; href: string }): (Node | string)[] {
  if (!link) return [text.replace('{off}', '')];
  const a = el('a', { href: link.href }, link.text);
  const at = text.indexOf('{off}');
  return at < 0 ? [text, ' ', a] : [text.slice(0, at), a, text.slice(at + 5)];
}

/**
 * At most 40 words above the fold (Matt, 2026-10-22 item 8; counted in the content): the question, "Why check?", the
 * three goals with their sublines (Matt's wording, 2026-10-23 02:08), the full approved privacy line (exempt from the
 * count, Matt 21:05; the counter line exempt too, Matt 2026-10-23 19:02).
 */
export function startScreen(s: StartSpec): HTMLElement {
  const { el, tr } = s;
  const goal = (id: string, icon: FlowIcon, title: string, sub: string, onClick: () => void, primary: boolean) =>
    btn(el, [flowIcon(icon), el('span', { class: 'fd-goal-text' }, el('span', { class: 'fd-goal-title' }, title), sub ? el('span', { class: 'fd-goal-sub' }, sub) : ''), flowIcon('chevron-right')],
      onClick, `fd-goal${primary ? ' primary' : ''}`, id);
  const { title, body } = splitPrivacyTitle(s.privacy.text);
  // Drop targets (computers only): while files are dragged over the screen the cards show a dashed outline, the one under
  // the pointer a filled one; dropping on a card starts that goal. A drop elsewhere on the screen is ignored, not opened
  // by the browser (which would leave the app).
  const section = el('section', { class: 'rv fd fd-screen fd-start', 'aria-labelledby': 'goal-heading' },
    el('h2', { id: 'goal-heading', tabindex: '-1', class: 'fd-title' }, tr('startD.headline')),
    // "Why check?" reopens the opening page (its figures and "More figures"), right under the question.
    s.onWhy ? el('p', { class: 'fd-why-check' }, btn(el, [tr('startD.whyCheck')], s.onWhy, 'link-button', 'why-check')) : '',
    el('div', { class: 'fd-goals' },
      // Every goal with a one-line subline (Matt's wording 2026-10-23 02:08, shortened by the less-text mockups, MATT GO 13:28;
      // the translate subline is held at today's wording until Matt picks a new one).
      goal('hero-payslip-start', 'receipt', tr('startD.payslip'), tr('startD.payslip.sub'), s.onPayslip, true),
      s.onForm106 ? goal('hero-form106', 'file-check', tr('startD.form106'), tr('startD.form106.sub'), s.onForm106, false) : '',
      goal('hero-translate', 'languages', tr('startD.translate'), tr('startD.translate.sub'), s.onTranslate, false)),
    // "More tools": the 4th row, a closed drawer (MATT GO 2026-10-26 13:33; docs/specs/more-tools.md): live tools only, no "coming soon".
    s.tools?.length ? el('details', { class: 'fd-card fd-fold fd-tools', id: 'more-tools' },
      el('summary', {}, el('span', {}, tr('startD.tools'))),
      el('ul', { class: 'fd-tools-list' }, ...s.tools.map((t) => el('li', {}, el('a', { class: 'fd-tool', id: t.id, href: `#/${t.route}` },
        el('span', { class: 'fd-goal-text' }, el('span', { class: 'fd-goal-title' }, t.title), t.sub ? el('span', { class: 'fd-goal-sub' }, t.sub) : ''), flowIcon('chevron-right')))))) : '',
    // The full approved privacy line, always shown (Matt, 2026-10-22 21:05: the user's first meeting with it; exempt from the
    // 40-word count by its class): its opener ("No sign-up. No names.") with the shield as the card's first line, the rest
    // under it at full width; the public counter line under it, visible without a tap (Matt, 2026-10-23 19:02).
    el('section', { class: 'fd-card fd-privacy-card', 'aria-label': tr('footer.privacy') },
      el('div', { class: 'fd-privacy-line' },
        title ? el('p', { class: 'fd-privacy-head' }, flowIcon('shield-check'), el('strong', {}, title)) : '',
        el('p', {}, ...(title ? [] : [flowIcon('shield-check'), ' ']), ...withLink(el, body, s.privacy.link))),
      // The public counter line, always visible (Matt, 2026-10-23 19:02: folded, a returning visitor, who skips the opening
      // page, never saw it); exempt from the 40-word count like the opening page's counter line.
      s.checks.length ? el('p', { class: 'fd-counter checks-so-far', id: 'fd-start-counter', 'data-fold-exempt': 'counter' }, flowIcon('banknote'), el('span', {}, ...s.checks)) : ''));
  if (s.onDropFiles && canDragDrop()) {
    const goals = section.querySelector('.fd-goals');
    const clear = () => {
      goals?.classList.remove('fd-dropping');
      section.querySelectorAll('.fd-goal.drop-over').forEach((b) => b.classList.remove('drop-over'));
    };
    const WHICH: Record<string, 'payslip' | 'form106' | 'translate'> = { 'hero-payslip-start': 'payslip', 'hero-form106': 'form106', 'hero-translate': 'translate' };
    section.addEventListener('dragenter', (e) => {
      if (dragHasFiles(e)) goals?.classList.add('fd-dropping');
    });
    section.addEventListener('dragover', (e) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      goals?.classList.add('fd-dropping');
      const card = (e.target as Element).closest?.('.fd-goal');
      section.querySelectorAll('.fd-goal.drop-over').forEach((b) => b !== card && b.classList.remove('drop-over'));
      if (card) {
        card.classList.add('drop-over');
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      } else if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    });
    section.addEventListener('dragleave', (e) => {
      if (!(e.relatedTarget instanceof Node) || !section.contains(e.relatedTarget)) clear();
    });
    section.addEventListener('drop', (e) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      const card = (e.target as Element).closest?.('.fd-goal');
      clear();
      const which = card ? WHICH[card.id] : undefined;
      if (which) s.onDropFiles?.(which, e.dataTransfer?.files ?? null);
    });
  }
  return section;
}
